import {
  ArrowRight,
  Globe,
  Gauge,
  Smartphone,
  BarChart3,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";

export const Hero = () => {
  const go = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
    >
      {/* background */}
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="absolute inset-0 radial-fade" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        {/* LEFT */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(182,245,0,0.35)] bg-[rgba(182,245,0,0.06)] text-[rgb(182,245,0)] text-xs sm:text-sm font-mono-alt">
            <Sparkles className="w-3.5 h-3.5" />
            We Build Websites That Drive Growth
          </div>

          <h1 className="font-display mt-7 text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] text-white">
            We Create <br />
            Websites That <br />
            <span className="text-[rgb(182,245,0)]">Grow Businesses</span>
          </h1>

          <p className="mt-7 text-white/65 text-base sm:text-lg max-w-xl leading-relaxed">
            We design, develop and optimize websites that not only look
            beautiful but also help your business attract more customers and
            grow faster.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-services-btn"
              onClick={() => go("services")}
              className="btn-lime px-6 py-3.5 rounded-full font-semibold text-sm sm:text-base inline-flex items-center gap-2"
            >
              Our Services <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-portfolio-btn"
              onClick={() => go("process")}
              className="btn-ghost px-6 py-3.5 rounded-full text-sm sm:text-base inline-flex items-center gap-2"
            >
              How We Work
            </button>
          </div>
        </motion.div>

        {/* RIGHT VISUAL */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative h-[520px] sm:h-[600px]"
          data-testid="hero-visual"
        >
          {/* orbit ring */}
          <div className="absolute inset-6 rounded-full border border-[rgba(182,245,0,0.18)]" />
          <div className="absolute inset-16 rounded-full border border-[rgba(182,245,0,0.1)]" />

          {/* main "browser" card */}
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-16 left-8 right-8 rounded-2xl bg-black border border-[rgba(182,245,0,0.35)] p-5 lime-glow"
          >
            <div className="flex items-center gap-1.5 mb-5">
              <span className="w-3 h-3 rounded-full bg-[rgb(182,245,0)]" />
              <span className="w-3 h-3 rounded-full bg-[rgba(182,245,0,0.5)]" />
              <span className="w-3 h-3 rounded-full bg-[rgba(182,245,0,0.25)]" />
            </div>
            <div className="font-display text-xl font-semibold text-white">
              Websites That
            </div>
            <div className="font-display text-xl font-semibold text-[rgb(182,245,0)] mb-3">
              Work. Grow. Succeed.
            </div>
            <p className="text-xs text-white/55 leading-relaxed">
              We craft fast, modern and user-friendly websites that turn
              visitors into loyal customers.
            </p>

            {/* fake chart */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-white/10 p-3">
                <div className="text-[10px] text-white/50 uppercase tracking-widest mb-2">
                  Preview
                </div>
                <div className="h-14 rounded-md bg-gradient-to-br from-[rgba(182,245,0,0.18)] to-transparent border border-[rgba(182,245,0,0.2)]" />
              </div>
              <div className="rounded-lg border border-white/10 p-3">
                <div className="text-[10px] text-white/50 uppercase tracking-widest mb-2">
                  Traffic
                </div>
                <div className="flex items-end gap-1 h-14">
                  {[30, 55, 40, 70, 50, 85, 95].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-sm bg-[rgb(182,245,0)]/80"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <button
              data-testid="hero-get-started-btn"
              onClick={() => go("contact")}
              className="mt-4 btn-lime px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2"
            >
              Get Started <ArrowRight className="w-3 h-3" />
            </button>
          </motion.div>

          {/* floating chips */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="absolute top-4 left-0 sm:left-4 surface rounded-xl px-3 py-3 flex items-center gap-2"
          >
            <div className="w-9 h-9 rounded-lg bg-[rgb(182,245,0)] flex items-center justify-center">
              <Globe className="w-5 h-5 text-black" />
            </div>
            <span className="text-xs text-white/90">Modern<br />Design</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="absolute top-2 right-0 sm:right-2 surface rounded-xl px-3 py-3 flex items-center gap-2"
          >
            <Gauge className="w-5 h-5 text-[rgb(182,245,0)]" />
            <span className="text-xs text-white/90">Speed<br />Optimized</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="absolute bottom-24 left-0 surface rounded-xl px-3 py-3 flex items-center gap-2"
          >
            <Smartphone className="w-5 h-5 text-[rgb(182,245,0)]" />
            <span className="text-xs text-white/90">Mobile<br />Friendly</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="absolute bottom-12 right-2 surface rounded-xl px-3 py-3 flex items-center gap-2"
          >
            <BarChart3 className="w-5 h-5 text-[rgb(182,245,0)]" />
            <span className="text-xs text-white/90">SEO<br />Ready</span>
          </motion.div>

          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-black border border-[rgba(182,245,0,0.5)] flex items-center justify-center lime-glow">
            <CheckCircle2 className="w-6 h-6 text-[rgb(182,245,0)]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
